// =====================================================================
// src/modulos/trocas.js — a tela Trocas Futuras (MdM `renderTrocas`, 6738)
//
// A conta está em `lib/trocas.js`; aqui é a marcação dele, div por div —
// movida de `modulos/reservas.js` em 27/09/2026 para servir a DOIS lugares:
//
//   · Escalas e folgas (hotel): "a função trocas futuras deveria estar na
//     mesma página do escala e folgas, uma vez que é a visualização que
//     ajuda a organizar a escala" (Hugo, 27/09). Os dados vêm de
//     `trocas_da_escala` (0068): quarto e datas, o hóspede só para quem tem
//     Hóspedes — sem telefone, sem plataforma, sem abrir a reserva;
//   · Reservas, só na visão da imobiliária ("para ela controlar seu
//     check-in e out na semana", Hugo, 20/09): a tabela `reservas`, que a
//     RLS corta às dela, com telefone, plataforma e a linha que abre a
//     reserva para quem edita.
//
// A janela anda de 7 em 7 dias para qualquer lado, então cada janela tem a
// sua busca: `carregar(ini, fim)` devolve toda reserva que sai OU chega
// dentro dela.
// =====================================================================
import { el, icone, abrirModal, fecharModal, aviso, enquantoAtualiza } from '../ui.js';
import { somarDias, dataBR, corDaPlataforma } from '../lib/reservas.js';
import { JANELA, calcularTrocas, corDaCarga, sugerirFolga, resumoDasTrocas, fmtDow, fmtDowLongoMaiusculo, fmtData, fmtDataLonga, rotuloDoPeriodo, ordenarPorQuarto, textoDaFolga, hospedeCurto } from '../lib/trocas.js';

// opções:
//   hoje()                     o dia do hotel
//   carregar(ini, fim)         as reservas da janela (Promise)
//   hospedeDe(r)               o nome do hóspede principal, ou null
//   extraDaNavegacao()         um elemento a mais na barra (o seletor de hotel do Sistema), ou null
//   abre(r)                    a linha do modal abre a reserva? (padrão: não)
//   aoAbrir(r, item)           o que fazer quando abre
//   comTelefone, comPlataforma o que a linha do modal mostra
//   id                         o id da raiz (padrão 'res-trocas')
export function telaDeTrocas(opcoes) {
  const o = { abre: () => false, aoAbrir: () => {}, hospedeDe: () => null, extraDaNavegacao: () => null, comTelefone: true, comPlataforma: true, id: 'res-trocas', ...opcoes };
  let ini = null, reservas = null;   // null = a janela começa hoje; null = ainda não buscou
  const raiz = el('div', { class: 'fade-in', id: o.id });

  const inicio = () => ini || o.hoje();
  async function carregar() {
    const i = inicio();
    reservas = await o.carregar(i, somarDias(i, JANELA - 1));
  }
  function recarregar() {
    return enquantoAtualiza(async () => {
      try { await carregar(); } catch (e) { aviso(e.message, 'erro'); return; }
      desenhar();
    });
  }
  // `_trocasShift`, `_trocasIrHoje`, `_trocasIrPara` (6621–6632)
  function ir(iso) { ini = iso; recarregar(); }
  const andar = n => ir(somarDias(inicio(), n));

  // um `<svg class="ic">` com as medidas inline que o MdM põe em cada uso
  function ic(nome, w = 16, va = -3, mr = 0) {
    const i = icone(nome);
    Object.assign(i.style, { width: w + 'px', height: w + 'px', verticalAlign: va + 'px', marginRight: mr ? mr + 'px' : '' });
    return i;
  }
  const dias = () => calcularTrocas(reservas || [], inicio(), { hospedeDe: o.hospedeDe });

  function desenhar() {
    raiz.replaceChildren();
    const i = inicio();
    const ds = dias();
    const tot = resumoDasTrocas(ds);
    const sugestao = sugerirFolga(ds);

    // o título e o subtítulo dele (6825)
    raiz.appendChild(el('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' } },
      el('div', null,
        el('h1', { style: { fontSize: '22px', fontWeight: '700', color: '#111827', margin: '0' } }, ic('ic-refresh', 13, -2, 5), 'Trocas Futuras'),
        el('div', { style: { fontSize: '12px', color: '#6b7280', marginTop: '3px' } }, 'Carga de limpeza por dia — para planejar folgas da equipe.'))));

    // navegação (6812): ◂ 7 dias · Hoje · 7 dias ▸ · Início
    const btn = { background: '#fff', border: '1px solid #d1d5db', borderRadius: '8px', padding: '8px 14px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', color: '#374151', minHeight: '38px' };
    raiz.appendChild(el('div', { id: 'tr-nav', style: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '10px 12px' } },
      el('button', { type: 'button', id: 'tr-antes', style: btn, onclick: () => andar(-7) }, '◂ 7 dias'),
      el('button', { type: 'button', id: 'tr-hoje', style: { ...btn, background: '#1B3A7A', color: '#fff', border: 'none' }, onclick: () => ir(null) }, 'Hoje'),
      el('button', { type: 'button', id: 'tr-depois', style: btn, onclick: () => andar(7) }, '7 dias ▸'),
      el('div', { style: { flex: '1', minWidth: '160px', display: 'flex', gap: '6px', alignItems: 'center' } },
        el('label', { for: 'tr-inicio', style: { fontSize: '12px', color: '#6b7280', fontWeight: '600' } }, 'Início:'),
        el('input', { type: 'date', id: 'tr-inicio', value: i, style: { border: '1px solid #d1d5db', borderRadius: '8px', padding: '7px 10px', fontSize: '13px', flex: '1', minHeight: '38px' }, onchange: ev => { if (ev.target.value) ir(ev.target.value); } })),
      o.extraDaNavegacao()));

    // sugestão de folga (6786)
    if (sugestao) {
      const t = textoDaFolga(sugestao);
      raiz.appendChild(el('div', { id: 'tr-folga', style: { background: 'linear-gradient(135deg,#dcfce7,#bbf7d0)', border: '2px solid #16a34a', borderRadius: '12px', padding: '14px 18px', marginBottom: '18px', display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' } },
        el('span', { style: { fontSize: '34px' } }, ic('ic-bulb')),
        el('div', { style: { flex: '1', minWidth: '200px' } },
          el('div', { style: { fontSize: '14px', fontWeight: '800', color: '#166534' } }, 'Sugestão de folga'),
          el('div', { style: { fontSize: '13px', color: '#166534', marginTop: '3px', lineHeight: '1.4' } },
            el('strong', null, t.dia), ' tem só ', el('strong', null, t.trocas), ' — é o dia mais leve da janela. Bom candidato pra folga da equipe de limpeza.'))));
    }

    // resumo em 4 cards (6799)
    const cardResumo = (rotulo, valor, cor, id, grande = true) => el('div', { style: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '12px', textAlign: 'center' } },
      el('div', { style: { fontSize: '11px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' } }, rotulo),
      grande ? el('div', { id, style: { fontSize: '24px', fontWeight: '800', color: cor, marginTop: '4px' } }, String(valor))
             : el('div', { id, style: { fontSize: '14px', fontWeight: '700', color: cor, marginTop: '8px', lineHeight: '1.2' } }, valor));
    raiz.appendChild(el('div', { id: 'tr-resumo', style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: '10px', marginBottom: '16px' } },
      cardResumo('Trocas (saídas)', tot.saidas, '#1B3A7A', 'tr-tot-saidas'),
      cardResumo('Chegadas', tot.chegadas, '#0e7490', 'tr-tot-chegadas'),
      cardResumo('Turnovers (saída+chegada)', tot.turnovers, '#b45309', 'tr-tot-turnovers'),
      cardResumo('Janela', rotuloDoPeriodo(i), '#374151', 'tr-janela', false)));

    // os 7 cards, um por dia (6757)
    raiz.appendChild(el('div', { id: 'tr-dias', style: { display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'stretch' } },
      ds.map(d => cardDoDia(d, tot))));

    // como ler (6832) — "flat" vira "quarto"
    raiz.appendChild(el('div', { style: { marginTop: '18px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '12px 14px', fontSize: '12px', color: '#6b7280', lineHeight: '1.6' } },
      el('strong', { style: { color: '#374151' } }, 'Como ler:'),
      ' "Trocas" = número de saídas no dia (cada saída exige limpeza). "Chegadas" = check-ins (preparo). "Turnover" = quarto com saída e chegada no mesmo dia (mais pressão na equipe). Cores comparam ',
      el('strong', null, 'esta janela'), ': 🟢 dia mais leve · 🟡 médio · 🔴 dia mais pesado. Reservas canceladas não contam. ',
      el('em', null, 'Clique num card pra ver os detalhes do dia.')));
  }

  function cardDoDia(d, tot) {
    const c = corDaCarga(d.workload, tot.minW, tot.maxW);
    const ehHoje = d.iso === o.hoje();
    const clicavel = d.saidas + d.chegadas > 0;
    return el('div', { dataset: { dia: d.iso, carga: c.label },
      style: { cursor: clicavel ? 'pointer' : '', flex: '1', minWidth: '130px', background: c.bg, border: '2px solid ' + c.border, borderRadius: '12px', padding: '12px 10px', textAlign: 'center', display: 'flex', flexDirection: 'column', transition: 'transform 0.1s ease', opacity: d.workload === 0 ? '0.85' : '' },
      onclick: clicavel ? () => modalDoDia(d.iso) : null,
      onmouseover: ev => { if (clicavel) ev.currentTarget.style.transform = 'translateY(-2px)'; },
      onmouseout: ev => { ev.currentTarget.style.transform = ''; } },
      el('div', { style: { fontSize: '11px', fontWeight: '700', color: c.text, textTransform: 'uppercase', letterSpacing: '0.5px' } }, fmtDow(d.dow),
        ehHoje ? el('span', { style: { background: '#1B3A7A', color: '#fff', fontSize: '9px', fontWeight: '700', padding: '2px 6px', borderRadius: '8px', marginLeft: '4px' } }, 'HOJE') : null),
      el('div', { style: { fontSize: '13px', fontWeight: '600', color: c.text, marginTop: '1px' } }, fmtData(d.iso)),
      el('div', { style: { fontSize: '32px', fontWeight: '800', color: c.text, margin: '8px 0 2px', lineHeight: '1' }, dataset: { saidas: String(d.saidas) } }, String(d.saidas)),
      el('div', { style: { fontSize: '10px', color: c.text, fontWeight: '600', marginBottom: '4px' } }, d.saidas === 1 ? 'troca' : 'trocas'),
      el('div', { style: { fontSize: '18px', lineHeight: '1' } }, c.emoji),
      el('div', { style: { fontSize: '10px', fontWeight: '700', color: c.text, marginTop: '2px' } }, c.label),
      el('div', { style: { fontSize: '10px', color: c.text, marginTop: '6px', borderTop: '1px dashed ' + c.border, paddingTop: '5px', lineHeight: '1.3' } },
        ic('ic-door'), ` ${d.saidas} saída${d.saidas !== 1 ? 's' : ''}`, el('br'),
        ic('ic-checkin'), ` ${d.chegadas} chegada${d.chegadas !== 1 ? 's' : ''}`,
        d.turnovers > 0 ? [el('br'), ic('ic-refresh', 13, -2, 5), `${d.turnovers} turnover${d.turnovers !== 1 ? 's' : ''}`] : null),
      d.detSaidas.length ? el('div', { style: { fontSize: '10px', color: c.text, opacity: '0.85', marginTop: '6px', lineHeight: '1.4', textAlign: 'left' } },
        d.detSaidas.slice(0, 4).flatMap((x, i) => [i ? el('br') : null, ic('ic-door'), ` ${x.flat} — ${hospedeCurto(x.hospede)}`]),
        d.detSaidas.length > 4 ? [el('br'), el('em', null, `+${d.detSaidas.length - 4} …`)] : null) : null);
  }

  // `modalDetalhesTrocas` (6855): as saídas e as chegadas do dia, com
  // hóspede, quarto, telefone e plataforma; a linha abre a reserva para
  // quem pode editá-la. Na escala (0068), só o quarto, as datas e — para
  // quem tem Hóspedes — o nome.
  function modalDoDia(iso) {
    const ds = dias();
    const d = ds.find(x => x.iso === iso); if (!d) return;
    const tot = resumoDasTrocas(ds);
    const c = corDaCarga(d.workload, tot.minW, tot.maxW);
    const turnover = Object.fromEntries(Object.keys(d.flatsSaida).filter(f => d.flatsChegada[f]).map(f => [f, true]));
    let algumaAbre = false;
    const linhaDet = (item, tipo) => {
      const r = (reservas || []).find(x => x.id === item.reservaId);
      const abre = !!r && !!o.abre(r);
      if (abre) algumaAbre = true;
      return el('div', { dataset: { reserva: item.reservaId, tipo }, style: { cursor: abre ? 'pointer' : '', display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 12px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', marginBottom: '6px', transition: 'background 0.1s' },
        onclick: abre ? () => { fecharModal(); o.aoAbrir(r, item); } : null,
        onmouseover: ev => { ev.currentTarget.style.background = '#f9fafb'; }, onmouseout: ev => { ev.currentTarget.style.background = '#fff'; } },
        el('div', { style: { fontSize: '18px', flexShrink: '0' } }, ic(tipo === 'saida' ? 'ic-door' : 'ic-checkin')),
        el('div', { style: { flex: '1', minWidth: '0' } },
          el('div', { style: { display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' } },
            el('strong', { style: { fontSize: '13px', color: '#111827' } }, item.flat),
            // a pílula da plataforma é a da Lista (corDaPlataforma), porque
            // as plataformas daqui são as da 0015, não as quatro do MdM
            o.comPlataforma ? el('span', { style: { background: corDaPlataforma(item.plataforma || 'direto'), color: '#fff', fontSize: '10px', fontWeight: '600', padding: '1px 6px', borderRadius: '6px', textTransform: 'uppercase', letterSpacing: '0.3px' } }, item.plataforma || 'Direto') : null,
            turnover[item.flatId] ? el('span', { style: { background: '#fef3c7', color: '#92400e', fontSize: '10px', fontWeight: '700', padding: '1px 6px', borderRadius: '6px' } }, ic('ic-refresh'), ' TURNOVER') : null),
          el('div', { style: { fontSize: '13px', color: '#374151', marginTop: '2px' } }, item.hospede),
          el('div', { style: { fontSize: '11px', color: '#6b7280', marginTop: '3px' } }, `${tipo === 'saida' ? 'Check-out' : 'Check-in'}: ${dataBR(item.checkIn)} → ${dataBR(item.checkOut)}`),
          // No MdM o telefone é um link `wa.me`. Aqui é texto: "não
          // utilize WhatsApp para nada até eu solicitar" (Hugo, 21/09;
          // CLAUDE.md §7).
          o.comTelefone ? el('div', { style: { marginTop: '4px' } },
            item.telefone
              ? el('span', { dataset: { telefone: '1' }, style: { fontSize: '11px', color: '#374151', display: 'inline-flex', alignItems: 'center', gap: '3px' } }, ic('ic-mobile'), ' ' + item.telefone)
              : el('span', { style: { fontSize: '11px', color: '#9ca3af', fontStyle: 'italic' } }, 'sem telefone')) : null));
    };
    const vazio = t => el('div', { style: { color: '#9ca3af', fontStyle: 'italic', fontSize: '12px', padding: '10px', textAlign: 'center' } }, t);
    const mini = (rotulo, valor, bg, borda, cor) => el('div', { style: { background: bg, border: '1px solid ' + borda, borderRadius: '8px', padding: '8px', textAlign: 'center' } },
      el('div', { style: { fontSize: '10px', color: cor === '#92400e' ? '#92400e' : '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.3px' } }, rotulo),
      el('div', { style: { fontSize: '22px', fontWeight: '800', color: cor, marginTop: '2px' } }, String(valor)));
    const secao = (icn, titulo, n, itens, tipo, semNada) => el('div', { style: { marginBottom: '16px' }, dataset: { secao: tipo } },
      el('h3', { style: { fontSize: '14px', fontWeight: '700', color: '#374151', margin: '0 0 8px' } }, ic(icn), ' ' + titulo + ' ', el('span', { style: { color: '#6b7280', fontWeight: '500', fontSize: '12px' } }, '— ' + n)),
      itens.length ? ordenarPorQuarto(itens).map(x => linhaDet(x, tipo)) : vazio(semNada));
    const saidas = secao('ic-door', 'Saídas (check-out)', d.saidas, d.detSaidas, 'saida', 'Sem saídas neste dia.');
    const chegadas = secao('ic-checkin', 'Chegadas (check-in)', d.chegadas, d.detChegadas, 'chegada', 'Sem chegadas neste dia.');
    const modal = el('div', { id: 'modal-trocas' },
      el('h2', { class: 'modal-title', style: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' } },
        el('span', null, ic('ic-reservas-dia')),
        el('span', null, `${fmtDowLongoMaiusculo(d.dow)}, ${fmtDataLonga(d.iso)}`),
        el('span', { style: { background: c.bg, border: '1.5px solid ' + c.border, color: c.text, fontSize: '11px', fontWeight: '700', padding: '3px 10px', borderRadius: '8px' } }, `${c.emoji} ${c.label}`)),
      el('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '8px', marginBottom: '14px' } },
        mini('Saídas', d.saidas, '#f9fafb', '#e5e7eb', '#1B3A7A'),
        mini('Chegadas', d.chegadas, '#f9fafb', '#e5e7eb', '#0e7490'),
        mini('Turnovers', d.turnovers, '#fef3c7', '#fcd34d', '#92400e')),
      saidas, chegadas,
      algumaAbre ? el('div', { style: { fontSize: '11px', color: '#6b7280', background: '#f3f4f6', borderRadius: '6px', padding: '8px 10px', marginTop: '10px', lineHeight: '1.5' } },
        ic('ic-bulb'), ' ', el('strong', null, 'Clique numa linha'), ' pra abrir a reserva e editar.') : null,
      el('div', { style: { display: 'flex', gap: '8px', marginTop: '14px' } },
        el('button', { type: 'button', class: 'btn btn-secondary', style: { flex: '1' }, onclick: fecharModal }, 'Fechar')));
    abrirModal(modal);
  }

  return {
    elemento: raiz,
    // busca a janela (se ainda não buscou) e desenha
    garantir() { if (reservas) { desenhar(); return Promise.resolve(); } return recarregar(); },
    recarregar,
    carregar,           // só busca (o módulo que hospeda redesenha)
    desenhar,
    esquecer() { reservas = null; },
    carregada: () => reservas !== null,
  };
}
